import { Container } from 'theme-ui';
import React, { useEffect, useState } from 'react';
import { useCart } from '@chordcommerce/react-autonomy';
import { logSentryError } from '~/sentry';
import Footer from '~/components/Footer';
import Nav from '~/components/Nav';
import { useLocalStorage } from '~/hooks/components/use-storage';
import CookiesMessage from '../Cookies/Message';
import useEventListener from '~/hooks/components/use-event-listener';
import useScrollTrackerAnalytics from '~/hooks/utils/customAnalytics/use-scroll-tracker-analytics';
import useNavigationHeight from '~/context/NavigationContext/useNavigationHeight';
import useSiteWideModal from '~/hooks/graphql/queries/use-site-wide-modal';
import { useLocation } from '@reach/router';
import EmailSignup from '../Generic/EmailSignup';
import useSiteWideBanner from '~/hooks/components/use-site-wide-banner';
import useFacebookPixelCartData from '~/hooks/components/use-facebook-pixel-cart-data';
import { useQueryParam } from 'use-query-params';

type Props = {
  children: React.ReactNode;
  transparentNav?: boolean;
  navColor?: boolean;
  backgroundColor?: string;
  removeInsiderSignup?: boolean;
};
const Layout = React.memo(function Layout({
  children,
  transparentNav,
  navColor,
  backgroundColor,
  removeInsiderSignup = false,
}: Props) {
  const { value: cartNumberValue, setValue: setCartChecker } = useLocalStorage('cart-number-check');
  const { value: cookiesMessageDismissed, setValue: setCookiesMessageDismissed } = useLocalStorage('cookies-message');
  const { value: affiliate, setValue: setAffiliate } = useLocalStorage('affiliate-code');
  const { value: hasVisited, setValue: setHasVisited } = useLocalStorage('has-visited');
  const { siteWideModal: modal, includeModalPaths } = useSiteWideModal();

  const { pathname, hash } = useLocation();

  const [shouldCookiesAppear, setShouldCookiesAppear] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useScrollTrackerAnalytics();

  const { loadCart, cart, addPromoCode } = useCart();
  useFacebookPixelCartData();

  useEventListener('scroll', () => {
    setTimeout(() => {
      setShouldCookiesAppear(true);
    }, 500);
  });

  useEffect(() => {
    if (scrollY !== 0 && !hash) {
      window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  }, [pathname, hash]);

  const [code] = useQueryParam('insider');

  useEffect(() => {
    if (cart?.lineItems?.length && (code || affiliate)) {
      (async () => {
        try {
          await addPromoCode({ promoCode: code ?? affiliate });
          setAffiliate(null);
        } catch (e) {
          // Can't inject promo code without changing the chord package's codes.
          logSentryError(e, { source: 'ADD_PROMO_CODE_ERROR', promoCode: code ?? affiliate });
          if (e instanceof Error) {
            if (/The coupon code you entered doesn't exist. Please try again./g.test(e?.message)) {
              setAffiliate(null);
            }
          }
        }
      })();
    } else {
      if (code) setAffiliate(code);
    }
  }, [affiliate, cart?.lineItems?.length]);

  // end.

  useEffect(() => {
    const loadTheCart = async () => {
      try {
        await loadCart();
      } catch (error) {
        console.error(error);
      }
    };

    if (!cartNumberValue) {
      setCartChecker(cart?.number);
    }

    loadTheCart();
  }, [loadCart]);

  const whitePaths = ['login', 'womens', 'mens', 'all', 'about'];
  const isWhite = whitePaths.find((wp) => pathname.includes(wp));

  const { navHeightRef } = useNavigationHeight();
  const { siteWideBanner } = useSiteWideBanner();

  return (
    <Container
      variant="fullwidth"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '--footer-height': ['13.5rem', null, '15rem'],
        '--nav-height': ['4.8rem', null, '6rem'],
        '--notification-height': siteWideBanner ? '4.4rem' : '0rem',
        '--product-nav-height': ['6.6rem', '7.8rem'],
        '--gutter-width': ['2.4rem', null, '4rem'],
        '--max-width': ['100%', null, '160rem'],
        minHeight: '100vh',
        height: '100%',
        position: 'relative',
      }}
    >
      <Nav
        ref={navHeightRef}
        navColor={navColor}
        transparentNav={transparentNav}
        isCartOpen={isCartOpen}
        setIsCartOpen={setIsCartOpen}
      />
      <main
        sx={{
          height: '100%',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          top: 0,
          bottom: 0,
          position: 'relative',
          backgroundColor: backgroundColor ?? isWhite ? 'white' : 'cloud',
        }}
      >
        <EmailSignup modal={modal} includeModalPaths={includeModalPaths} />
        {children}
      </main>

      {!cookiesMessageDismissed && (
        <CookiesMessage setCookiesMessageDismissed={setCookiesMessageDismissed} isVisible={shouldCookiesAppear} />
      )}

      <Footer removeInsiderSignup={removeInsiderSignup} />
    </Container>
  );
});

export default Layout;
